/**
 * "Become a Partner" application form — opened from My Workspaces.
 * Submission creates a partner_applications row for platform admin review.
 */
import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import Modal from './Modal';
import { useApi } from '../lib/api';

export default function BecomePartnerModal({ open, onClose, onSubmitted }) {
  const api = useApi();
  const { user } = useUser();

  const initialContactName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  const initialContactEmail = user?.emailAddresses?.[0]?.emailAddress || '';

  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState(initialContactName);
  const [contactEmail, setContactEmail] = useState(initialContactEmail);
  const [contactPhone, setContactPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('');
  const [panOrTaxId, setPanOrTaxId] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setCompanyName(''); setContactPhone(''); setWebsite(''); setCountry('');
    setPanOrTaxId(''); setNotes(''); setError(null); setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!companyName.trim()) return setError('Company name is required');
    if (!contactName.trim()) return setError('Contact name is required');
    if (!contactEmail.trim()) return setError('Contact email is required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) return setError('Enter a valid contact email');
    if (website.trim() && !/^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/i.test(website.trim())) return setError('Enter a valid website, e.g. example.com');

    setSubmitting(true);
    try {
      await api.submitPartnerApplication({
        companyName: companyName.trim(),
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        contactPhone: contactPhone.trim() || undefined,
        website: website.trim() || undefined,
        country: country.trim() || undefined,
        panOrTaxId: panOrTaxId.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onSubmitted?.();
      reset();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to submit application');
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="Become a Partner" onClose={handleClose}>
      <form onSubmit={handleSubmit} className="create-app-form">
        <label className="form-label">
          <span>Company name <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
          <input className="form-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} maxLength={200} autoFocus />
        </label>
        <label className="form-label">
          <span>Contact name <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
          <input className="form-input" value={contactName} onChange={(e) => setContactName(e.target.value)} maxLength={200} />
        </label>
        <label className="form-label">
          <span>Contact email <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
          <input className="form-input" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} maxLength={200} />
        </label>
        <label className="form-label">
          Phone (optional)
          <input
            className="form-input"
            type="tel"
            inputMode="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value.replace(/[^\d+\s-]/g, '').slice(0, 15))}
            maxLength={15}
          />
        </label>
        <label className="form-label">
          Website (optional)
          <input className="form-input" value={website} onChange={(e) => setWebsite(e.target.value)} maxLength={300} />
        </label>
        <label className="form-label">
          Country (optional)
          <input className="form-input" value={country} onChange={(e) => setCountry(e.target.value)} maxLength={100} />
        </label>
        <label className="form-label">
          PAN / Tax ID (optional)
          <input className="form-input" value={panOrTaxId} onChange={(e) => setPanOrTaxId(e.target.value)} maxLength={50} />
        </label>
        <label className="form-label">
          Notes (optional)
          <textarea className="form-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
        </label>

        {error && <div className="form-error">{error}</div>}

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Application'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
